import api, { handleResponse, handleError } from './api';

const authService = {
  /**
   * Register a new user
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @param {string} phoneNumber - User's phone number
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async register(email, password, phoneNumber) {
    try {
      const response = await api.post('/auth/register', {
        email,
        password,
        phoneNumber,
      });

      // API returns { success, data: { accessToken, refreshToken, user, expiresAt } }
      const result = response.data.data || response.data;

      // Store tokens if returned
      if (result.accessToken) {
        localStorage.setItem('accessToken', result.accessToken);
      }
      if (result.refreshToken) {
        localStorage.setItem('refreshToken', result.refreshToken);
      }

      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Login a user
   * @param {string} email - User's email
   * @param {string} password - User's password
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async login(email, password) {
    try {
      const response = await api.post('/auth/login', {
        email,
        password,
      });

      // API returns { success, data: { accessToken, refreshToken, user, expiresAt } }
      const result = response.data.data || response.data;

      // Store tokens
      if (result.accessToken) {
        localStorage.setItem('accessToken', result.accessToken);
      }
      if (result.refreshToken) {
        localStorage.setItem('refreshToken', result.refreshToken);
      }

      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Logout the current user
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async logout() {
    try {
      const refreshToken = localStorage.getItem('refreshToken');

      // Call logout endpoint if we have a refresh token
      if (refreshToken) {
        await api.post('/auth/logout', { refreshToken });
      }

      // Clear local storage
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');

      return {
        success: true,
        data: { message: 'Logged out successfully' },
        error: null,
      };
    } catch (error) {
      // Still clear local storage even if API call fails
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');

      return handleError(error);
    }
  },

  /**
   * Refresh the access token
   * @param {string} token - The refresh token
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async refreshToken(token) {
    try {
      const response = await api.post('/auth/refresh', {
        refreshToken: token,
      });

      // API returns { success, data: { accessToken, refreshToken, user, expiresAt } }
      const result = response.data.data || response.data;

      // Store new tokens
      if (result.accessToken) {
        localStorage.setItem('accessToken', result.accessToken);
      }
      if (result.refreshToken) {
        localStorage.setItem('refreshToken', result.refreshToken);
      }

      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Request a password reset email
   * @param {string} email - User's email
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async forgotPassword(email) {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },

  /**
   * Reset password with token
   * @param {string} token - Password reset token
   * @param {string} newPassword - New password
   * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
   */
  async resetPassword(token, newPassword) {
    try {
      const response = await api.post('/auth/reset-password', {
        token,
        newPassword,
      });
      return handleResponse(response);
    } catch (error) {
      return handleError(error);
    }
  },
};

export default authService;
